/**
 * Confirmation for an irreversible or money-moving action, stating its
 * consequence in numbers (UX 1.0a, UI-alus §1 "Dialoog"). Replaces
 * `window.confirm` / `window.prompt`.
 *
 * Rules it enforces so callers cannot get them wrong:
 *  - the consequence is listed line by line with its figures (sum, hours) —
 *    the caller formats them; this component never computes money;
 *  - `confirmLabel` is a verb ("Tühista vahetus"), not OK/Yes;
 *  - Cancel is never disabled and never the default. While the confirm call
 *    is in flight the server will finish it regardless, so the button then
 *    reads "Sulge" (close), not "Katkesta" (cancel), and a line under the
 *    buttons says the action is under way and will complete. Closing then
 *    just closes: the late result is dropped, and no "cancelled" state is
 *    shown, because nothing was cancelled;
 *  - destructive: `error` colour, and the confirm button does NOT get initial
 *    focus (focus lands on the dialog itself, or the reason field);
 *  - an async `onConfirm` keeps its progress and its failure INSIDE the
 *    dialog; the dialog closes only on success;
 *  - < 600 px: buttons are a full-width stack, confirm on top; ≥ 600 px: a row
 *    on the right with confirm at the edge;
 *  - Esc and the backdrop close it, and focus returns to the trigger (MUI's
 *    focus trap + restore).
 *
 * The failure text comes from the caller's `errorText` (the 1.0b mapping);
 * without one the dialog shows a generic sentence, never `err.message`.
 */

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export interface ConsequenceLine {
  /** Translated label ("Tasu"). */
  label: string;
  /** Translated, already formatted value with its figure ("45,00 €", "8 h"). */
  value: string;
}

export interface ConsequenceReason {
  /** Translated field label. */
  label: string;
  /** Minimum trimmed length before confirm is enabled. Default 1. */
  minLength?: number;
  helperText?: string;
}

export interface ConsequenceDialogProps {
  open: boolean;
  /** Translated question/title. */
  title: string;
  /** What will happen, line by line with figures. */
  consequence: readonly ConsequenceLine[];
  /** Optional translated sentence(s) above the lines. */
  description?: ReactNode;
  /** Translated verb for the confirm button. */
  confirmLabel: string;
  /** Translated override for the cancel button. */
  cancelLabel?: string;
  destructive?: boolean;
  /** Ask for a reason; `onConfirm` receives it trimmed. `true` uses the default label. */
  requireReason?: boolean | ConsequenceReason;
  /** May be async; a rejection keeps the dialog open with the error inside. */
  onConfirm: (reason?: string) => void | Promise<unknown>;
  /** Cancel, Esc, backdrop — and after a successful confirm. */
  onClose: () => void;
  /** Translate a failure (pass the 1.0b mapping). Never return `err.message`. */
  errorText?: (err: unknown) => string;
}

export function ConsequenceDialog({
  open,
  title,
  consequence,
  description,
  confirmLabel,
  cancelLabel,
  destructive = false,
  requireReason,
  onConfirm,
  onClose,
  errorText,
}: ConsequenceDialogProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const bodyId = useId();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  // Each opening is its own attempt: a result arriving after Cancel, or after
  // the dialog was reopened, must not close or error the new one.
  const attempt = useRef(0);

  useEffect(() => {
    if (open) {
      setBusy(false);
      setError(null);
      setReason('');
    }
    attempt.current += 1;
  }, [open]);

  const reasonSpec: ConsequenceReason | null = requireReason
    ? requireReason === true
      ? { label: t('common:ui.consequenceDialog.reason', { defaultValue: 'Põhjus' }) }
      : requireReason
    : null;
  // Destructive without a reason field: nothing is pre-focused that one Enter
  // could fire. Focus the title instead, so the question is read out first.
  // A callback ref, because the title mounts inside a portal a render after
  // `open` flips; MUI's focus trap then sees focus already inside and keeps it.
  const focusTitle = destructive && !reasonSpec;
  const titleRef = useCallback(
    (node: HTMLHeadingElement | null) => {
      if (node && focusTitle) node.focus();
    },
    [focusTitle],
  );

  const reasonOk = !reasonSpec || reason.trim().length >= (reasonSpec.minLength ?? 1);

  const confirm = async () => {
    if (busy || !reasonOk) return;
    const mine = attempt.current;
    setBusy(true);
    setError(null);
    try {
      await onConfirm(reasonSpec ? reason.trim() : undefined);
      if (attempt.current !== mine) return;
      setBusy(false);
      onClose();
    } catch (err) {
      if (attempt.current !== mine) return;
      setBusy(false);
      setError(
        errorText?.(err)
          ?? t('common:ui.consequenceDialog.failed', {
            defaultValue: 'Toiming ei õnnestunud. Midagi ei muutunud — proovi uuesti.',
          }),
      );
    }
  };

  const fullWidthOnPhone = { width: { xs: '100%', sm: 'auto' }, m: 0 } as const;

  return (
    <Dialog
      open={open}
      onClose={() => onClose()}
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          mx: { xs: 2, sm: 4 },
          mb: { xs: 'max(16px, env(safe-area-inset-bottom, 0px))', sm: 4 },
          width: { xs: 'calc(100% - 32px)', sm: undefined },
        },
      }}
    >
      <DialogTitle
        id={titleId}
        ref={titleRef}
        tabIndex={-1}
        sx={{ fontWeight: 700, '&:focus': { outline: 'none' } }}
      >
        {title}
      </DialogTitle>
      <DialogContent>
        <Box id={bodyId}>
          {description && (
            // A div, not a <p>: a description may carry a field (a reason
            // picker, a quantity) and a <p> cannot contain block content.
            <Typography variant="body1" component="div" sx={{ mb: 1.5 }}>
              {description}
            </Typography>
          )}
          {consequence.length > 0 && (
            <Box component="dl" sx={{ m: 0, display: 'grid', rowGap: 0.75 }}>
              {consequence.map((line, i) => (
                <Box
                  key={`${line.label}-${i}`}
                  sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}
                >
                  <Typography component="dt" variant="body1" color="text.secondary">
                    {line.label}
                  </Typography>
                  <Typography
                    component="dd"
                    variant="body1"
                    sx={{ m: 0, fontWeight: 600, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {line.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
        {reasonSpec && (
          <TextField
            label={reasonSpec.label}
            helperText={reasonSpec.helperText}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            multiline
            minRows={2}
            fullWidth
            autoFocus
            sx={{ mt: 2 }}
          />
        )}
        {error && (
          <Alert severity="error" role="alert" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions
        disableSpacing
        sx={{
          px: 3,
          pb: 2,
          gap: 1,
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'flex-end',
        }}
      >
        <Button type="button" onClick={() => onClose()} sx={fullWidthOnPhone}>
          {busy
            ? t('common:ui.consequenceDialog.close', { defaultValue: 'Sulge' })
            : (cancelLabel ?? t('common:ui.consequenceDialog.cancel', { defaultValue: 'Katkesta' }))}
        </Button>
        <Button
          type="button"
          variant="contained"
          color={destructive ? 'error' : 'primary'}
          onClick={() => void confirm()}
          disabled={!reasonOk}
          aria-busy={busy || undefined}
          autoFocus={!destructive && !reasonSpec}
          startIcon={busy ? <CircularProgress size={18} color="inherit" aria-hidden /> : undefined}
          sx={fullWidthOnPhone}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
      {/* Mounted for the dialog's lifetime so the busy line is announced. */}
      <Box role="status" sx={{ px: 3, pb: busy ? 2 : 0 }}>
        {busy && (
          <Typography variant="body2" color="text.secondary">
            {t('common:ui.consequenceDialog.inProgress', {
              defaultValue: 'Toiming on käimas ja viiakse lõpuni.',
            })}
          </Typography>
        )}
      </Box>
    </Dialog>
  );
}
